import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import { executeOrQueue } from "@/offline/executeOrQueue";
import { useOffline } from "@/offline/OfflineProvider";
import { toApiId } from "@/services/http/mappers";
import type { FarmDetail, StaffDetail } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

type CoverageMode = "ALL" | "SELECTED";

export default function VisitFormPage() {
  const offline = useOffline();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    visitDate: "",
    farmerId: "",
    farmId: "",
    staffId: "",
    observations: "",
    recommendations: "",
    pest: "",
    followUpDate: "",
    followUpAction: "",
  });
  const [coverageMode, setCoverageMode] = useState<CoverageMode>("ALL");
  const [selectedPlotIds, setSelectedPlotIds] = useState<string[]>([]);
  const [farms, setFarms] = useState<FarmDetail[]>([]);
  const [staff, setStaff] = useState<StaffDetail[]>([]);
  const [referenceLoading, setReferenceLoading] = useState(true);
  const [referenceError, setReferenceError] = useState("");
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    setReferenceLoading(true);
    Promise.all([
      services.farms.list({ pageSize: 100 }),
      services.staff.list({ pageSize: 100 }),
    ])
      .then(([farmResult, staffResult]) => {
        if (!active) return;
        setFarms(farmResult.items);
        setStaff(staffResult.items);
        setReferenceError("");
      })
      .catch((reason: Error) => {
        if (active) setReferenceError(reason.message || "Field-operation reference data could not be loaded.");
      })
      .finally(() => {
        if (active) setReferenceLoading(false);
      });
    return () => { active = false; };
  }, []);

  const farmers = useMemo(() => {
    const seen = new Map<string, string>();
    farms.forEach((farm) => {
      if (!seen.has(farm.farmerId)) seen.set(farm.farmerId, farm.farmerName);
    });
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [farms]);

  const farmerFarms = useMemo(
    () => farms.filter((farm) => farm.farmerId === form.farmerId && farm.farmStatus === "ACTIVE"),
    [farms, form.farmerId],
  );
  const selectedFarm = farmerFarms.find((farm) => farm.id === form.farmId);
  const activePlots = useMemo(
    () => (selectedFarm?.plotsData || []).filter((plot) => plot.status === "ACTIVE"),
    [selectedFarm],
  );

  const assignableStaff = useMemo(
    () => staff.filter((member) => {
      if (member.employmentStatus !== "ACTIVE") return false;
      const role = `${member.projectAssignment?.roleOnProject || ""} ${member.jobTitle || ""}`.toUpperCase();
      return role.includes("AGRONOMIST") || role.includes("FIELD_OFFICER") || role.includes("FIELD OFFICER");
    }),
    [staff],
  );

  const applyFarmSelection = (farmId: string, mode: CoverageMode = coverageMode) => {
    const farm = farms.find((item) => item.id === farmId);
    const plots = (farm?.plotsData || []).filter((plot) => plot.status === "ACTIVE");
    setForm((current) => ({ ...current, farmId }));
    setSelectedPlotIds(mode === "ALL" ? plots.map((plot) => plot.id) : []);
  };

  const selectFarmer = (farmerId: string) => {
    const candidateFarms = farms.filter((farm) => farm.farmerId === farmerId && farm.farmStatus === "ACTIVE");
    const onlyFarm = candidateFarms.length === 1 ? candidateFarms[0] : undefined;
    setForm((current) => ({ ...current, farmerId, farmId: onlyFarm?.id || "" }));
    setSelectedPlotIds(
      coverageMode === "ALL" && onlyFarm
        ? onlyFarm.plotsData.filter((plot) => plot.status === "ACTIVE").map((plot) => plot.id)
        : [],
    );
  };

  const changeCoverageMode = (mode: CoverageMode) => {
    setCoverageMode(mode);
    setSelectedPlotIds(mode === "ALL" ? activePlots.map((plot) => plot.id) : []);
  };

  const togglePlot = (plotId: string, checked: boolean) => {
    setSelectedPlotIds((current) => checked
      ? Array.from(new Set([...current, plotId]))
      : current.filter((id) => id !== plotId));
  };

  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const next = [
      !form.visitDate && "Visit date is required.",
      !form.farmerId && "Farmer is required.",
      !form.farmId && "Farm is required.",
      selectedPlotIds.length === 0 && "Select at least one Plot for this visit.",
      !form.staffId && "Assigned agronomist or field officer is required.",
      !form.observations.trim() && "Observations are required.",
    ].filter(Boolean) as string[];
    setErrors(next);
    if (next.length) return;

    const farmer = farmers.find((item) => item.id === form.farmerId);
    const farm = farms.find((item) => item.id === form.farmId);
    const selectedPlots = activePlots.filter((plot) => selectedPlotIds.includes(plot.id));
    const assignedStaff = staff.find((member) => member.id === form.staffId);
    const entityId = crypto.randomUUID();
    const capturedAt = new Date().toISOString();
    const isOffline = offline.simulatedOffline || !offline.online;

    const domainInput = {
      visitDate: form.visitDate,
      farmerId: form.farmerId,
      farmerName: farmer?.name || "Farmer",
      farmId: form.farmId,
      farmName: farm?.name || "Farm",
      plotId: selectedPlotIds[0],
      plotName: selectedPlots[0]?.name,
      plotIds: selectedPlotIds,
      plotNames: selectedPlots.map((plot) => plot.name),
      assignedStaffId: form.staffId,
      assignedStaffName: assignedStaff?.name || "Assigned staff",
      status: "PLANNED" as const,
      observations: form.observations,
      recommendations: form.recommendations,
      pestDiseaseObservation: form.pest || undefined,
      followUpDate: form.followUpDate || undefined,
      followUpAction: form.followUpAction || undefined,
    };
    const apiPayload = {
      client_generated_id: entityId,
      visit_date: form.visitDate,
      actual_activity_datetime: capturedAt,
      local_created_at: capturedAt,
      created_offline: isOffline,
      farmer_id: toApiId(form.farmerId),
      farm_id: toApiId(form.farmId),
      plot_id: toApiId(selectedPlotIds[0]),
      plot_ids: selectedPlotIds.map(toApiId),
      assigned_staff_id: toApiId(form.staffId),
      observations: form.observations,
      recommendations: form.recommendations,
      pest_disease_observation: form.pest || undefined,
      follow_up_date: form.followUpDate || undefined,
      follow_up_action: form.followUpAction || undefined,
    };
    const result = await executeOrQueue({
      offline: isOffline,
      request: { entityType: "FIELD_VISIT", entityId, method: "POST", path: "/visits", payload: apiPayload },
      execute: () => services.visits.create(domainInput, entityId),
    });
    if (result.mode === "QUEUED") {
      toast("Visit queued for synchronization", { description: "The Plot-linked visit will retry after reconnect." });
      navigate("/visits");
      return;
    }
    toast("Visit planned", { description: `Linked to ${selectedPlotIds.length} Plot${selectedPlotIds.length === 1 ? "" : "s"} in the active ARISE environment.` });
    navigate(`/visits/${result.value.id}`);
  };

  return (
    <div className="space-y-6" data-testid="visit-form-page">
      <PageHeader eyebrow="Field operations" title="Plan Field Visit" description="Create a visit against the real farmer, Farm and one, some or all active Plots." backTo="/visits" />
      {referenceError && <Card className="border-destructive/30"><CardContent className="p-4 text-sm text-destructive">{referenceError}</CardContent></Card>}
      {errors.length > 0 && <Card className="border-destructive/30" data-testid="form-error-summary"><CardContent className="p-4"><p className="font-semibold text-destructive">Complete the required fields</p><ul className="mt-2 list-disc pl-5 text-sm text-muted-foreground">{errors.map((error) => <li key={error}>{error}</li>)}</ul></CardContent></Card>}

      <form onSubmit={submit} className="space-y-5" data-testid="visit-create-form">
        <section className="form-section">
          <h2 className="text-lg font-semibold">Visit links & assignment</h2>
          <p className="mt-1 text-sm text-muted-foreground">Farm and Plot choices come directly from the ARISE staging registry.</p>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="visit-date">Visit date</Label>
              <Input id="visit-date" type="date" value={form.visitDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("visitDate", e.target.value)} data-testid="visit-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Farmer</Label>
              <Select value={form.farmerId} onValueChange={selectFarmer} disabled={referenceLoading}>
                <SelectTrigger data-testid="visit-farmer-select"><SelectValue placeholder={referenceLoading ? "Loading farmers..." : "Select farmer"} /></SelectTrigger>
                <SelectContent>{farmers.map((farmer) => <SelectItem key={farmer.id} value={farmer.id}>{farmer.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Farm</Label>
              <Select value={form.farmId} onValueChange={(value: string) => applyFarmSelection(value)} disabled={!form.farmerId || farmerFarms.length === 0}>
                <SelectTrigger data-testid="visit-farm-select"><SelectValue placeholder="Select Farm" /></SelectTrigger>
                <SelectContent>{farmerFarms.map((farm) => <SelectItem key={farm.id} value={farm.id}>{farm.name}</SelectItem>)}</SelectContent>
              </Select>
              {form.farmerId && farmerFarms.length === 1 && <p className="text-xs text-muted-foreground">The farmer's canonical Farm is selected automatically.</p>}
            </div>
            <div className="space-y-2">
              <Label>Assigned agronomist / field officer</Label>
              <Select value={form.staffId} onValueChange={(value: string) => set("staffId", value)} disabled={referenceLoading}>
                <SelectTrigger data-testid="visit-staff-select"><SelectValue placeholder="Assign field staff" /></SelectTrigger>
                <SelectContent>{assignableStaff.map((member) => <SelectItem key={member.id} value={member.id}>{member.name} · {member.projectAssignment?.roleOnProject || member.jobTitle}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6 rounded-lg border p-4" data-testid="visit-plot-coverage">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold">Plot coverage</p>
                <p className="text-sm text-muted-foreground">Record whether the visit covers every active Plot or selected Plots only.</p>
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={coverageMode === "ALL" ? "default" : "outline"} onClick={() => changeCoverageMode("ALL")} disabled={!form.farmId}>All active Plots</Button>
                <Button type="button" size="sm" variant={coverageMode === "SELECTED" ? "default" : "outline"} onClick={() => changeCoverageMode("SELECTED")} disabled={!form.farmId}>Selected Plots</Button>
              </div>
            </div>
            {!form.farmId ? <p className="mt-4 text-sm text-muted-foreground">Select the Farm first.</p>
              : activePlots.length === 0 ? <p className="mt-4 text-sm text-warning">This Farm has no active Plots yet. Add its Plot records from Farms before scheduling a field visit.</p>
              : <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {activePlots.map((plot) => {
                  const checked = selectedPlotIds.includes(plot.id);
                  return <label key={plot.id} className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm">
                    <input type="checkbox" className="mt-1 size-4" checked={checked} disabled={coverageMode === "ALL"} onChange={(event) => togglePlot(plot.id, event.target.checked)} data-testid={`visit-plot-checkbox-${plot.id}`} />
                    <span><span className="font-medium">{plot.name}</span><span className="block text-xs text-muted-foreground">{plot.code}{plot.localityName ? ` · ${plot.localityName}` : ""}{plot.reportedAreaAcres ? ` · ${plot.reportedAreaAcres} acres` : ""}</span></span>
                  </label>;
                })}
              </div>}
            {form.farmId && activePlots.length > 0 && <p className="mt-3 text-xs text-muted-foreground">{selectedPlotIds.length} of {activePlots.length} active Plot{activePlots.length === 1 ? "" : "s"} selected.</p>}
          </div>
        </section>

        <section className="form-section">
          <h2 className="text-lg font-semibold">Observations & follow-up</h2>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2"><Label htmlFor="visit-observations">Observations</Label><Textarea id="visit-observations" value={form.observations} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => set("observations", e.target.value)} data-testid="visit-observations-input" /></div>
            <div className="space-y-2 md:col-span-2"><Label htmlFor="visit-recommendations">Agronomic recommendations</Label><Textarea id="visit-recommendations" value={form.recommendations} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => set("recommendations", e.target.value)} data-testid="visit-recommendations-input" /></div>
            <div className="space-y-2 md:col-span-2"><Label htmlFor="visit-pest">Pest/disease observation</Label><Textarea id="visit-pest" value={form.pest} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => set("pest", e.target.value)} data-testid="visit-pest-input" /></div>
            <div className="space-y-2"><Label htmlFor="visit-follow-up-date">Follow-up date</Label><Input id="visit-follow-up-date" type="date" value={form.followUpDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("followUpDate", e.target.value)} data-testid="visit-follow-up-date-input" /></div>
            <div className="space-y-2"><Label htmlFor="visit-follow-up-action">Follow-up action</Label><Input id="visit-follow-up-action" value={form.followUpAction} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("followUpAction", e.target.value)} data-testid="visit-follow-up-action-input" /></div>
          </div>
        </section>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/visits")} data-testid="visit-cancel-button">Cancel</Button>
          <Button type="submit" disabled={referenceLoading || !!referenceError} data-testid="visit-submit-button"><Save className="mr-2 size-4" />Save visit</Button>
        </div>
      </form>
    </div>
  );
}
